import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import {
  AuthenticationType,
  EntraIdConnection,
  EntraIdConnectionHandlerService,
  GrantType,
  ManagedIdentityAuthentication,
  ManagedIdentityType,
  ModernAuthentication
} from "@coso/connection-forms";
import { FormText, ViewMode, FormBuilder, FormGroup } from "@coso/forms";
import { LoadingService } from "@coso/loading";
import { IS_SAAS } from "@coso/utility";
import { forkJoin, Subject } from "rxjs";
import { filter, take, takeUntil } from "rxjs/operators";
import { AUserPermissionsService } from "../../../services/a-user-permissions.service";
import { ConnectionMapperService } from "../../../services/connection-mapper.service";
import { EntraIdConnectionService } from "../../../services/entra-id-connection.service";
import { EnvironmentService } from "../../../services/environment.service";
import { ReplicationService } from "../../../services/replication.service";
import { handleError } from "../../../utils/handle-error";
import { isRunning } from "../../../utils/is-running";
import { IS_DOCKER } from "@coso-replicator/core";
import { Validators } from "@angular/forms";

@Component({
  standalone:false,
  templateUrl: "./entra-id.component.html",
  styleUrls: ["./entra-id.component.scss"]
})
export class EntraIdComponent implements OnInit, OnDestroy {
  private unsubscribe = new Subject<void>();

  instanceId: string;
  defaultClientId: string;
  defaultAdministrativeUnitClientId: string;

  get title() {
    if (this.isConfigured) {
      if (this.isReadOnly) {
        return "View Microsoft Entra ID connection";
      } else {
        return "Edit Microsoft Entra ID connection";
      }
    } else {
      return "Create Microsoft Entra ID connection";
    }
  }
  isConfigured = false;
  isReadOnly: boolean;
  isRunning: boolean;
  ViewMode = ViewMode;
  errorMessage: string = undefined;
  formGroup: FormGroup<EntraIdConnection>;
  formText: FormText<EntraIdConnection>;

  constructor(
    @Inject(IS_DOCKER) public isDocker: boolean,
    @Inject(IS_SAAS) public isSaaS: boolean,
    public loadingService: LoadingService,
    private fb: FormBuilder,
    public entraIdConnectionHandler: EntraIdConnectionHandlerService,
    private router: Router,
    private route: ActivatedRoute,
    private entraIdConnectionService: EntraIdConnectionService,
    private connectionMapperService: ConnectionMapperService,
    private userPermissionsService: AUserPermissionsService,
    private replicationService: ReplicationService,
    private environmentService: EnvironmentService
  ) {}

  ngOnDestroy(): void {
    this.unsubscribe.next();
    sessionStorage.removeItem("instance-id");
  }

  ngOnInit() {
    this.instanceId = this.route.snapshot.paramMap.get("instance-id");
    sessionStorage.setItem("instance-id", this.instanceId);
    forkJoin([this.replicationService.getStatus(this.instanceId), this.userPermissionsService.hasLimitedAccess$, this.userPermissionsService.isCustomerAgent$]).subscribe(
      ([status, hasLimitedAccess, isCustomerAgent]) => {
        this.isRunning = isRunning(status);
        this.isReadOnly = hasLimitedAccess || isCustomerAgent || isRunning(status);
      },
      (error) => (this.errorMessage = `Something went wrong due to: ${handleError(error)}`)
    );

    this.generateFormGroup();
    this.loadConnection();

    this.formGroup.controls.modernAuthentication.controls.clientId.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.setDefaultClientId());
  }

  private generateFormGroup() {
    this.formGroup = this.fb.group<EntraIdConnection>({
      id: this.fb.control(null),
      administrativeUnitId: this.fb.control(null),
      authenticationType: this.fb.control(AuthenticationType.Modern),
      modernAuthentication: this.fb.group<ModernAuthentication>({
        tenantId: this.fb.control(null),
        azureEndpoint: this.fb.control("https://login.microsoftonline.com/"),
        clientId: this.fb.control(null),
        certificateThumbprint: this.fb.control(null),
        useDefaultCertificate: this.fb.control(true, [Validators.required]),
        grantType: this.fb.control(GrantType.ClientCredentials),
        clientSecret: this.fb.control(null),
        scope: this.fb.control("offline_access"),
        encryptedToken: this.fb.control(null),
        changeAzureAdApp: this.fb.control(false),
        useDefaultAzureAdApp: this.fb.control(this.isSaaS),
        // The internal scope for which the grant is considered valid for SaaS services (sent on state parameter for SaaS ConsentCallback management service).
        saasConsentCallbackGrantScope: this.fb.control(this.instanceId),
        apiPermission: this.fb.control(null),
      }),
      managedIdentityAuthentication: this.fb.group<ManagedIdentityAuthentication>({
        type: this.fb.control(ManagedIdentityType.UserAssigned),
        clientId: this.fb.control(null)
      })
    });
  }

  saveConnection() {
    if (this.isReadOnly) {
      return;
    }
    this.entraIdConnectionHandler
      .testConnectionResultFor$(this.formGroup.value.internalId)
      .pipe(
        filter((r) => !!r && r.isSuccess),
        take(1)
      )
      .subscribe((r) => {
        this.formGroup.controls.id.setValue(r.payload);
        this.save();
      });

    this.entraIdConnectionHandler
      .isTestConnectionSuccessfulFor$(this.formGroup.value.internalId)
      .pipe(take(1))
      .subscribe((result) => {
        if (!result) {
          this.entraIdConnectionHandler.testConnection(this.formGroup.getRawValue());
        }
      });
  }

  returnToList(message?: string) {
    this.router.navigate(["../"], {
      relativeTo: this.route,
      state: { message }
    });
  }

  private save() {
    this.errorMessage = undefined;
    this.loadingService.start("Saving Microsoft Entra ID connection");
    const connection = this.connectionMapperService.mapFromEntraIdConnection(this.formGroup.getRawValue());
    this.entraIdConnectionService.upsertConnection(this.instanceId, connection).subscribe(
      () => {
        this.returnToList("Microsoft Entra ID  connection was successfully saved.");
        this.loadingService.stop();
      },
      (error) => {
        this.errorMessage = `Failed to save Microsoft Entra ID  connection due to: ${handleError(error)}`;
        this.loadingService.stop();
      }
    );
  }

  private loadConnection() {
    this.loadingService.start("Loading Microsoft Entra ID connection", "loadEntraIdConnection");
    forkJoin([this.environmentService.getAzureAdDefaultClientId(), this.environmentService.getAzureAdAdministrativeUnitDefaultClientId(), this.entraIdConnectionService.getConnection(this.instanceId)]).subscribe(
      ([defaultClientId, defaultAdministrativeUnitClientId, data]) => {

        this.defaultClientId = defaultClientId;
        this.defaultAdministrativeUnitClientId = defaultAdministrativeUnitClientId;

        const connection = this.connectionMapperService.mapToEntraIdConnection(data);
        const configuredClientId = connection.modernAuthentication.clientId;
        const defaultClientIdForConnection = connection.administrativeUnitId ? defaultAdministrativeUnitClientId : defaultClientId;
        const useDefaultAzureAdApp = this.isSaaS && (!configuredClientId || configuredClientId.toLowerCase() === defaultClientIdForConnection?.toLowerCase());

        this.isConfigured = !!data.server ||
         connection.authenticationType === AuthenticationType.ManagedIdentity ||
          (connection.authenticationType === AuthenticationType.Modern && !!connection.modernAuthentication?.clientId);

        this.formGroup.controls.modernAuthentication.controls.useDefaultAzureAdApp.setValue(useDefaultAzureAdApp);
        this.formGroup.patchValue(connection);
        this.setDefaultClientId();

        if (connection.authenticationType === AuthenticationType.ManagedIdentity && connection.managedIdentityAuthentication.clientId) {
          this.formGroup.controls.managedIdentityAuthentication.controls.type.setValue(ManagedIdentityType.UserAssigned);
          this.formGroup.controls.managedIdentityAuthentication.controls.clientId.setValue(connection.managedIdentityAuthentication.clientId);
        }

        if (!this.isConfigured) {
          this.formGroup.controls.modernAuthentication.controls.clientSecret.setValidators([Validators.required]);
        }

        this.formGroup.controls.modernAuthentication.controls.changeAzureAdApp.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
          if (value) {
            this.formGroup.controls.modernAuthentication.controls.clientSecret.setValidators([Validators.required]);
          } else {
            this.formGroup.controls.modernAuthentication.controls.clientSecret.clearValidators();
          }
          this.formGroup.controls.modernAuthentication.controls.clientSecret.updateValueAndValidity();
        })

        this.formGroup.controls.administrativeUnitId.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.setDefaultClientId());
        this.formGroup.controls.modernAuthentication.controls.clientId.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe(() => this.setDefaultClientId());

        this.loadingService.stop("loadEntraIdConnection");
      },
      (error) => {
        this.errorMessage = `Failed to get Microsoft Entra ID connection due to: ${handleError(error)}`;
        this.loadingService.stop("loadEntraIdConnection");
      }
    );
  }

  private getDefaultClientId() : string {
    return this.formGroup?.controls?.administrativeUnitId?.value ? this.defaultAdministrativeUnitClientId : this.defaultClientId;
  }

  private setDefaultClientId() {
    let clientId = this.getDefaultClientId();

    if (clientId &&
        this.formGroup.controls.modernAuthentication.controls.useDefaultAzureAdApp?.value &&
        this.formGroup.controls.modernAuthentication.controls.clientId.value != clientId
      ) {
      this.formGroup.controls.modernAuthentication.controls.clientId.setValue(clientId);
    }
  }
}
